/**
 * mobile-menu-contrast.js — contrast-aware fallback for mobile-menu fragments.
 *
 * A fragment's CSS slice deliberately leaves THEME vars to the host so the
 * drawer adapts to the project palette (MM-4 fix #1). That adoption has no
 * contrast guarantee: casablanca paints drop links `color: var(--primary)` on
 * a `background-color: var(--primaryDark)` panel, which is cream-on-brown
 * (8.70:1) on its own theme but 2.61:1 on uicompass — because `--primaryDark`
 * is DERIVED from `--primary` by darkening (theme-tokens deriveExtendedThemeColors),
 * so the pair collapses on every theme whose primary is already dark.
 *
 * This module resolves each declared drawer fg/bg pair against the HOST theme
 * and, only when the resolved pair falls below threshold, emits an override
 * that re-colors the foreground to the most readable available theme token.
 *
 * Pure. The CSS reader is INJECTED (csstree in node, CSSOM in the browser),
 * mirroring the merge engine's two-engine convention — this module never
 * parses CSS with regex and never touches the DOM.
 */

import { resolveCssColorValue, parseCssColor } from './color-utils.js';
import { contrastRatio } from './wcag-contrast.js';
import { DEFAULT_THEME, deriveExtendedThemeColors } from './theme-tokens.js';

/** WCAG AA for normal-size body text. Drawer links are not reliably "large". */
export const CONTRAST_THRESHOLD = 4.5;

/**
 * The single declared registry of drawer foreground/background pairs. Class
 * names are the CANONICAL_DRAWER_CLASSES from the activation contract; each
 * entry names the class whose `color` is at risk and the class whose
 * `background-color` sits behind it.
 *
 * ORDER IS SEMANTIC: most specific first. A drop link carries BOTH
 * `.cs-li-link` and `.cs-drop-link` (`.cs-li-link.cs-drop-link` is casablanca's
 * real selector), so a rule is claimed by the FIRST pair whose fgClass it
 * mentions and never re-examined by a later, broader one. Without that,
 * `li-link` reads the drop link's color against the wrapper's background and
 * "repairs" a pair that does not exist.
 */
export const DRAWER_CONTRAST_PAIRS = Object.freeze([
  Object.freeze({ id: 'drop-link', fgClass: 'cs-drop-link', bgClass: 'cs-drop-ul' }),
  Object.freeze({ id: 'li-link', fgClass: 'cs-li-link', bgClass: 'cs-ul-wrapper' })
]);

/**
 * Foreground tokens tried when a pair collapses, best-contrast-first among
 * whichever clear the threshold. Plain hexes are the last resort so a theme
 * missing every token still yields a legible drawer.
 */
export const FALLBACK_FOREGROUNDS = Object.freeze([
  'var(--bodyTextColorWhite)',
  'var(--bodyTextColor)',
  'var(--headerColor)',
  '#fff',
  '#000'
]);

/** Rule reader over css-tree (node: parsers, tests, thumbnail generator). */
export function createCssTreeRuleReader(csstree) {
  return function readRules(css) {
    const out = [];
    let ast;
    try {
      ast = csstree.parse(css || '');
    } catch {
      return null;
    }
    csstree.walk(ast, {
      visit: 'Rule',
      enter(node) {
        const selectorText = csstree.generate(node.prelude);
        const decls = {};
        csstree.walk(node.block, {
          visit: 'Declaration',
          enter(d) {
            decls[d.property.toLowerCase()] = {
              value: csstree.generate(d.value).trim(),
              important: !!d.important
            };
          }
        });
        out.push({ selectorText, decls });
      }
    });
    return out;
  };
}

/** Rule reader over CSSOM (browser: the live merge path). */
export function createCssomRuleReader() {
  return function readRules(css) {
    let sheet;
    try {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(css || '');
    } catch {
      return null;
    }
    const out = [];
    const visit = (rules) => {
      for (const rule of rules) {
        if (rule.cssRules && !(rule instanceof CSSStyleRule)) {
          visit(rule.cssRules);
          continue;
        }
        if (!(rule instanceof CSSStyleRule)) continue;
        const decls = {};
        for (const prop of rule.style) {
          decls[prop.toLowerCase()] = {
            value: rule.style.getPropertyValue(prop).trim(),
            important: rule.style.getPropertyPriority(prop) === 'important'
          };
        }
        out.push({ selectorText: rule.selectorText, decls });
      }
    };
    visit(sheet.cssRules);
    return out;
  };
}

/** Host theme → a flat variable map color-utils can resolve var() chains through. */
function themeVariables(theme) {
  const explicit = theme && typeof theme === 'object' ? theme : {};
  return { ...DEFAULT_THEME, ...deriveExtendedThemeColors(explicit), ...explicit };
}

function toRgb(value, variables) {
  const resolved = resolveCssColorValue(value, variables);
  return resolved ? parseCssColor(resolved) : null;
}

/** Selector mentions the class as a compound token (not a substring match). */
function mentionsClass(selectorText, className) {
  return new RegExp(`\\.${className}(?![\\w-])`).test(selectorText);
}

/**
 * Last rule in source order wins for our purposes: these slices are already
 * flattened to one scope, so later-and-important is what the browser paints.
 */
function winningDeclaration(rules, className, property, excludeClasses = []) {
  let winner = null;
  for (const rule of rules) {
    if (!mentionsClass(rule.selectorText, className)) continue;
    if (excludeClasses.some((other) => mentionsClass(rule.selectorText, other))) continue;
    const decl = rule.decls[property];
    if (!decl) continue;
    if (!winner || decl.important || !winner.important) {
      winner = { ...decl, selectorText: rule.selectorText };
    }
  }
  return winner;
}

/**
 * Derive contrast-repair CSS for a fragment slice on a given host theme.
 *
 * A pair is only repaired when it PASSES on the fragment's own source theme and
 * FAILS on the host theme — that difference is the theme-adoption bug this
 * module exists for. A pair that fails on the source theme too is evidence the
 * static fg/bg model is wrong (an intermediate background, an overlay
 * pseudo-element, a cascade this module cannot see), not that the source
 * template ships an illegible drawer — those render correctly, and MM-4
 * visually confirmed several. Measured on the real corpus (10 fragments x 187
 * template themes): that one rule takes 782/1870 raw fires down to 510 repairs,
 * suppressing 272 — and it zeroes out every fragment that fired against its own
 * source theme (mosaicbishoparts 187/187 and shatteredvisionautoglass 85/187,
 * both of which render correctly in MM-4's confirmed thumbnails).
 * Without `sourceTheme` the check cannot run and the caller gets the raw,
 * over-firing result, so callers should always pass it.
 *
 * @param {string} fragmentCss  the fragment's CSS slice, already host-scoped
 * @param {object} theme        the project theme token map
 * @param {{readRules:Function, threshold?:number, sourceTheme?:object}} options
 * @returns {{css:string, findings:Array}} `css` is '' when nothing collapsed
 */
export function deriveContrastOverrides(
  fragmentCss,
  theme,
  { readRules, threshold, sourceTheme } = {}
) {
  const limit = Number.isFinite(threshold) ? threshold : CONTRAST_THRESHOLD;
  const findings = [];
  if (typeof readRules !== 'function') return { css: '', findings };

  const rules = readRules(fragmentCss || '');
  if (!rules) return { css: '', findings: [{ pair: null, reason: 'css-parse-failed' }] };

  const variables = themeVariables(theme);
  const overrides = [];

  for (const [index, pair] of DRAWER_CONTRAST_PAIRS.entries()) {
    // Claimed by the first (most specific) pair — see the registry's note.
    const moreSpecific = DRAWER_CONTRAST_PAIRS.slice(0, index).map((p) => p.fgClass);
    const fg = winningDeclaration(rules, pair.fgClass, 'color', moreSpecific);
    const bg = winningDeclaration(rules, pair.bgClass, 'background-color');
    if (!fg || !bg) continue;

    const bgRgb = toRgb(bg.value, variables);
    const fgRgb = toRgb(fg.value, variables);
    if (!bgRgb || !fgRgb) continue;

    const ratio = contrastRatio(fgRgb, bgRgb);
    if (!Number.isFinite(ratio) || ratio >= limit) continue;

    // Falsifier: if this pair is illegible on the fragment's OWN theme, the
    // model is wrong rather than the host. Stay out and say so.
    if (sourceTheme) {
      const sourceVars = themeVariables(sourceTheme);
      const sourceFg = toRgb(fg.value, sourceVars);
      const sourceBg = toRgb(bg.value, sourceVars);
      const sourceRatio = sourceFg && sourceBg ? contrastRatio(sourceFg, sourceBg) : Number.NaN;
      if (!Number.isFinite(sourceRatio) || sourceRatio < limit) {
        findings.push({
          pair: pair.id,
          reason: 'source-theme-also-fails',
          ratio: Number(ratio.toFixed(2)),
          sourceRatio: Number.isFinite(sourceRatio) ? Number(sourceRatio.toFixed(2)) : null
        });
        continue;
      }
    }

    // Prefer the EARLIEST candidate that clears the threshold — the list is
    // ordered token-first so a repaired drawer still tracks the project theme
    // (a raw #fff wins on raw ratio but stops adapting). Only if none clears
    // does the highest-contrast candidate win, so the reason code is honest.
    let best = null;
    let cleared = null;
    for (const candidate of FALLBACK_FOREGROUNDS) {
      const rgb = toRgb(candidate, variables);
      if (!rgb) continue;
      const candidateRatio = contrastRatio(rgb, bgRgb);
      if (!Number.isFinite(candidateRatio)) continue;
      if (!best || candidateRatio > best.ratio) best = { value: candidate, ratio: candidateRatio };
      if (!cleared && candidateRatio >= limit)
        cleared = { value: candidate, ratio: candidateRatio };
    }
    if (cleared) best = cleared;
    if (!best || best.ratio < limit) {
      findings.push({ pair: pair.id, reason: 'no-readable-fallback', ratio });
      continue;
    }

    // Echo the LOSING rule's own selector so the override matches its
    // specificity exactly; appended last + !important, so it wins the cascade.
    overrides.push(`${fg.selectorText} { color: ${best.value} !important; }`);
    findings.push({
      pair: pair.id,
      reason: 'contrast-repaired',
      ratio: Number(ratio.toFixed(2)),
      repairedRatio: Number(best.ratio.toFixed(2)),
      from: fg.value,
      to: best.value
    });
  }

  return { css: overrides.join('\n'), findings };
}
